import React from 'react';
import { AbsoluteFill, OffthreadVideo, Sequence, useCurrentFrame, useRemotionEnvironment, useVideoConfig } from 'remotion';
import type { ProjectModel, VideoItem } from '@video-studio/shared';

/**
 * Videolaag.
 *
 * Twee dingen zijn hier essentieel:
 *
 * 1. `trimBefore` bepaalt het startpunt in het bronbestand, in frames van de
 *    bron. Wij rekenen de opgeslagen projectframes om via de fps-verhouding,
 *    zodat een bron met 25 fps in een project met 30 fps op de juiste plek
 *    begint.
 * 2. `<Sequence from durationInFrames>` bepaalt waar de clip op de tijdlijn
 *    staat. Alleen binnen dat venster speelt de video, waardoor knippunten
 *    exact blijven en er niets buiten de clip doorklinkt.
 *
 * De audio van de clip loopt mee via `volume`, inclusief fade in/out.
 *
 * Preview en export gebruiken dit component allebei. Voor de export levert de
 * worker eerst een tussenbestand op de project-fps, zodat `trimBefore` 1:1
 * klopt; in de preview spelen we de proxy af.
 */
export interface VideoLaagProps {
  item: VideoItem;
  project: ProjectModel;
  assetUrls: Record<string, string>;
  /** fps van het bronbestand, indien bekend; anders 1:1 met de project-fps. */
  bronFps?: number | null;
}

/**
 * De voorvertoning mag geen `#t=`-fragment aan de URL plakken.
 *
 * Remotion doet dat anders zelf. Chrome gaat dan niet gewoon vooruit afspelen,
 * maar zoekt steeds opnieuw. Een bestand dat in een gewone speler vloeiend is,
 * hapert daardoor in de studio. Een bestaand hekje laat Remotion dat fragment
 * overslaan. Bij het renderen blijft de kale URL staan, want ffmpeg leest die.
 */
export function voorvertoningSrc(url: string, renderen: boolean): string {
  if (renderen || url.includes('#')) return url;
  return `${url}#voorvertoning`;
}

export function berekenTrimBefore(
  item: Pick<VideoItem, 'bronStartFrame'>,
  projectFps: number,
  bronFps: number | null | undefined,
): number {
  if (!bronFps || bronFps <= 0 || !Number.isFinite(bronFps)) {
    return Math.max(0, Math.round(item.bronStartFrame));
  }
  return Math.max(0, Math.round((item.bronStartFrame * bronFps) / projectFps));
}

export const VideoLaag: React.FC<VideoLaagProps> = ({ item, project, assetUrls, bronFps }) => {
  const url = assetUrls[item.assetId];
  const trimBefore = berekenTrimBefore(item, project.fps, bronFps);

  return (
    <Sequence
      from={Math.max(0, Math.round(item.startFrame))}
      durationInFrames={Math.max(1, Math.round(item.duurInFrames))}
      name={item.naam || 'Video'}
    >
      <VideoInSequence item={item} url={url} trimBefore={trimBefore} />
    </Sequence>
  );
};

const VideoInSequence: React.FC<{
  item: VideoItem;
  url: string | undefined;
  trimBefore: number;
}> = ({ item, url, trimBefore }) => {
  const frameInSeq = useCurrentFrame();
  const { fps } = useVideoConfig();
  const { isRendering } = useRemotionEnvironment();

  if (!url) {
    return <OntbrekendeMedia tekst={`Media ontbreekt: ${item.naam || item.assetId}`} />;
  }

  const volume = berekenVolume(item, frameInSeq, fps);

  return (
    <AbsoluteFill>
      <OffthreadVideo
        src={voorvertoningSrc(url, isRendering)}
        trimBefore={trimBefore}
        volume={item.gedempt ? 0 : volume}
        playbackRate={item.snelheid}
        muted={item.gedempt}
        // In de voorvertoning niet wachten op een nieuw beeld: dat zet de hele
        // speler stil en laat hem daarna opnieuw zoeken. Bij het renderen
        // maakt het niet uit, daar wordt per frame uitgelezen.
        pauseWhenBuffering={isRendering}
        acceptableTimeShiftInSeconds={isRendering ? undefined : 30}
        style={{
          width: '100%',
          height: '100%',
          objectFit: passingNaarObjectFit(item.passing),
        }}
      />
    </AbsoluteFill>
  );
};

export function passingNaarObjectFit(passing: VideoItem['passing']): React.CSSProperties['objectFit'] {
  switch (passing) {
    case 'bijsnijden':
      return 'cover';
    case 'uitrekken':
      return 'fill';
    case 'bevatten':
    default:
      return 'contain';
  }
}

/**
 * Volume met fade in/out, als lineaire interpolatie over het opgegeven aantal
 * frames. De worker doet bij de basistrack hetzelfde met ffmpeg `afade`, zodat
 * preview en export hetzelfde klinken.
 */
export function berekenVolume(
  item: Pick<VideoItem, 'volume' | 'fadeInFrames' | 'fadeOutFrames' | 'duurInFrames'>,
  frameInSeq: number,
  _fps: number,
): number {
  let volume = item.volume;
  const eind = item.duurInFrames - 1;
  if (item.fadeInFrames > 0 && frameInSeq < item.fadeInFrames) {
    volume *= Math.max(0, Math.min(1, frameInSeq / item.fadeInFrames));
  }
  if (item.fadeOutFrames > 0 && frameInSeq > eind - item.fadeOutFrames) {
    const resterend = Math.max(0, eind - frameInSeq);
    volume *= Math.max(0, Math.min(1, resterend / item.fadeOutFrames));
  }
  // Boven 1 versterken we niet in de browser; dat is een platformbeperking.
  return Math.max(0, Math.min(1, volume));
}

export const OntbrekendeMedia: React.FC<{ tekst: string }> = ({ tekst }) => {
  return (
    <AbsoluteFill
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(180, 20, 20, 0.35)',
        color: '#ffffff',
        fontFamily: 'sans-serif',
        fontSize: 28,
        textAlign: 'center',
        padding: 40,
      }}
    >
      {tekst}
    </AbsoluteFill>
  );
};
